'use client';

import { useLazyResetQuery } from '@/src/api/api';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import passwordValidator from '@/src/utils/passwordValidator';
import PasswordValidation from './PasswordValidation';
import useParamsEditor from '@/src/utils/useParamsEditor';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import ModalTitle from '@components/ModalTitle';

export default function ResetPasswordForm() {
  const t = useTranslations('ResetPasswordForm');

  const { register, handleSubmit, formState } = useForm();
  const searchParams = useSearchParams();
  const editParam = useParamsEditor();
  const [message, setMessage] = useState('');
  const [resetQuery, { isLoading: isReseting, isError: isResetingError }] =
    useLazyResetQuery();
  const [resetErrorsHTML, setResetErrorsHTML] = useState(<></>);
  const [passwordErrors, setPasswordErrors] = useState(
    'length lower upper digit special'
  );
  const resetSubmit = (formData: unknown) => {
    resetQuery(formData).then((res) => {
      if (res.isSuccess) {
        if (res.data.status === 200) {
          editParam({
            modal: 'login',
            password_changed: 'true',
            email: null,
            token: null,
          });
          return;
        }
        setMessage(res.data.message);
      }
    });
  };

  const resetPasswordValidator = (password: string) => {
    let result = passwordValidator(password);
    if (!result) {
      result = '';
    }
    setPasswordErrors(result);
  };
  return (
    <div className='w-full'>
      <ModalTitle title={t('title')} />
      <form
        method='post'
        onSubmit={handleSubmit(resetSubmit)}
        className='mb-8 flex w-full flex-col gap-4 border-b-[1px] border-b-white/30 pb-8'
      >
        <input
          type='hidden'
          {...register('email', {
            value: searchParams.get('email'),
            required: { value: true, message: t('validation.emailRequired') },
          })}
        />
        <input
          type='hidden'
          {...register('token', {
            value: searchParams.get('token'),
            required: { value: true, message: t('validation.tokenRequired') },
          })}
        />
        <label htmlFor='password'>{t('label.password')}</label>
        <input
          type='password'
          id='password'
          className='block w-full appearance-none rounded-sm p-2 text-dark'
          {...register('password', {
            required: {
              value: true,
              message: t('validation.passwordRequired'),
            },
            onChange: (e) => {
              resetPasswordValidator(e.currentTarget.value);
            },
            validate: passwordValidator,
          })}
        />
        <PasswordValidation passwordValidation={passwordErrors} />
        <label htmlFor='password_confirmation'>
          {t('label.confirmPassword')}
        </label>
        <input
          type='password'
          id='password_confirmation'
          className='block w-full appearance-none rounded-sm p-2 text-dark'
          {...register('password_confirmation', {
            required: {
              value: true,
              message: t('validation.passwordConfirmationRequired'),
            },
            validate: (value, formValues) => {
              if (value !== formValues.password) {
                return t('validation.passwordConfirmationNotMatch');
              }
            },
          })}
        />
        <>
          {formState.errors.password_confirmation &&
            formState.errors.password_confirmation.message}
        </>
        <input
          type='submit'
          className='mb-1 mt-4 w-full appearance-none rounded-sm border border-brand bg-brand p-2 font-bold uppercase text-black transition-all hover:bg-transparent hover:text-white'
        />
        {isReseting && t('loading')}
        {message}
      </form>
    </div>
  );
}
